import { useState, useMemo, useRef } from 'react'
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { 
  CheckCircle, 
  LayoutGrid, 
  Upload, 
  Library,
  Loader2,
  RefreshCw,
  Plus,
  Image as ImageIcon,
  Sparkles
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useNotes } from '@/hooks/useNotes'
import { useCollage } from '@/hooks/useCollage'
import { useR2Upload } from '@/lib/r2'
import { useAssetAnalysis, useImageGeneration } from '@/hooks/useCopywriting'
import { useAssets } from '@/hooks/useAssets'
import { compressImages } from '@/lib/compress'
import type { Asset, NoteImage } from '@/types'
import type { CollageLayout } from '@/lib/collage'
import { toast } from 'sonner'

export interface AssetPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  assets: Asset[]
  projectId: string
  onSelect: (payload: Partial<NoteImage>) => void
}

const COLLAGE_LAYOUTS: { id: CollageLayout; label: string }[] = [
  { id: '2x1', label: '横双' },
  { id: '1x2', label: '竖双' },
  { id: '2x2', label: '四宫' },
  { id: '3x1', label: '横三' },
  { id: '1x3', label: '竖三' },
  { id: '3x3', label: '九宫' },
]

/**
 * 素材选择器组件
 * 支持从素材库选择、在线拼图、本地上传三种方式
 */
export function AssetPicker({
  open,
  onOpenChange,
  assets,
  projectId,
  onSelect,
}: AssetPickerProps) {
  const [activeTab, setActiveTab] = useState<string>('library')
  const [selectedLibraryAsset, setSelectedLibraryAsset] = useState<Asset | null>(null)
  const { notes } = useNotes(projectId)
  const { addAssets, updateAssetsAnalysis, updateMultipleAnalysisStatus } = useAssets(projectId)
  const { analyzeAssets } = useAssetAnalysis()
  
  // ========== 拼图状态 ==========
  const [selectedCollageAssets, setSelectedCollageAssets] = useState<Asset[]>([])
  const [selectedLayout, setSelectedLayout] = useState<CollageLayout>('2x2')
  const { isGenerating: isCollageGenerating, previewUrl, generatePreview, generateFile } = useCollage(projectId)
  
  // ========== 上传状态 ==========
  const [syncToLibrary, setSyncToLibrary] = useState(true)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [activeUploads, setActiveUploads] = useState(0)
  const { startUpload, isUploading } = useR2Upload({
    onUploadError: (err) => toast.error(err.message || '上传失败'),
  })

  // ========== AI 生图状态 ==========
  const [selectedAiGenAssets, setSelectedAiGenAssets] = useState<Asset[]>([])
  const [aiGenPrompt, setAiGenPrompt] = useState('')
  const [aiGenPreviewUrl, setAiGenPreviewUrl] = useState<string | null>(null)
  const { generateImage, isGenerating: isAiGenerating } = useImageGeneration()

  // 提取所有笔记中使用的素材 ID
  const assetIdsInNotes = useMemo(() => {
    const ids = new Set<string>()
    notes.forEach(note => {
      note.images?.forEach(img => {
        if (img.asset_ids) img.asset_ids.forEach(id => ids.add(id))
      })
    })
    return ids
  }, [notes])

  // ========== 拼图逻辑 ==========
  const handleToggleCollageAsset = (asset: Asset) => {
    setSelectedCollageAssets(prev => {
      const exists = prev.find(a => a.id === asset.id)
      if (exists) return prev.filter(a => a.id !== asset.id)
      return [...prev, asset]
    })
  }

  const handleGenerateCollage = async () => {
    if (selectedCollageAssets.length === 0) {
      toast.error('请先选择素材')
      return
    }
    const urls = selectedCollageAssets.map(a => a.url)
    await generatePreview(urls, selectedLayout)
  }

  const handleConfirmCollage = async () => {
    if (!previewUrl) return
    
    try {
      // 生成拼图文件并上传
      const file = await generateFile(
        selectedCollageAssets.map(a => a.url),
        selectedLayout,
        `collage-${Date.now()}.jpg`
      )
      const uploadRes = await startUpload([file])
      if (!uploadRes?.[0]) throw new Error('上传失败')
      
      const url = uploadRes[0].ufsUrl
      
      onSelect({
        type: 'collage',
        source: 'collage',
        url,
        asset_ids: selectedCollageAssets.map(a => a.id),
        status: 'completed'
      })
    } catch (err) {
      toast.error('添加拼图失败')
    }
  }

  // ========== AI 生图逻辑 ==========
  const handleToggleAiGenAsset = (asset: Asset) => {
    setSelectedAiGenAssets(prev => {
      const exists = prev.find(a => a.id === asset.id)
      if (exists) return prev.filter(a => a.id !== asset.id)
      if (prev.length >= 9) {
        toast.error('最多选择 9 张素材')
        return prev
      }
      return [...prev, asset]
    })
  }

  const handleGenerateAiImage = async () => {
    if (!aiGenPrompt.trim()) {
      toast.error('请输入 Prompt')
      return
    }
    
    try {
      const url = await generateImage(aiGenPrompt, {
        aspect_ratio: '3:4',
        source_images: selectedAiGenAssets.map(a => a.url),
        prompt_type: 'generate-image-manual'
      })
      setAiGenPreviewUrl(url)
    } catch (err) {
      toast.error('AI 生成失败')
    }
  }

  const handleConfirmAiImage = async () => {
    if (!aiGenPreviewUrl) return
    
    try {
      let finalUrl = aiGenPreviewUrl

      // 如果是 base64，需要上传
      if (aiGenPreviewUrl.startsWith('data:')) {
        const res = await fetch(aiGenPreviewUrl)
        const blob = await res.blob()
        const file = new File([blob], `ai-gen-${Date.now()}.jpg`, { type: 'image/jpeg' })
        const uploadRes = await startUpload([file])
        if (!uploadRes?.[0]) throw new Error('上传失败')
        finalUrl = uploadRes[0].ufsUrl
      }

      onSelect({
        type: 'single',
        source: 'ai_generated',
        url: finalUrl,
        asset_ids: selectedAiGenAssets.map(a => a.id),
        status: 'completed'
      })
    } catch (err) {
      toast.error('添加 AI 生成图片失败')
    }
  }

  // ========== 上传逻辑 ==========
  const handleUploadClick = () => fileInputRef.current?.click()

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    
    const file = files[0]
    if (!file.type.startsWith('image/')) {
      toast.error('请选择图片文件')
      return
    }

    try {
      setActiveUploads(prev => prev + 1)
      const [compressed] = await compressImages([file])
      
      const uploadRes = await startUpload([compressed])
      if (!uploadRes?.[0]) throw new Error('上传失败')
      
      const url = uploadRes[0].ufsUrl
      
      let assetId: string | undefined
      if (syncToLibrary) {
        const records = await addAssets([{
          filename: uploadRes[0].name,
          url,
          thumbnail_url: url,
          analysis_status: 'analyzing'
        }])
        assetId = records[0].id
        // 后台分析
        analyzeAssets(records).then(async (result) => {
          if (result?.analyses?.[0]) {
            await updateAssetsAnalysis([{
              id: result.analyses[0].id,
              description: result.analyses[0].description,
              title: result.analyses[0].suggested_title || file.name.replace(/\.[^.]+$/, ''),
              tags: result.analyses[0].tags || [],
              scene: result.analyses[0].scene,
              mood: result.analyses[0].mood,
              composition: result.analyses[0].composition,
              pure_hue: result.analyses[0].pure_hue,
              tonality: result.analyses[0].tonality,
            }])
          }
        }).catch(err => {
          console.error('分析失败:', err)
          updateMultipleAnalysisStatus([records[0].id], 'failed')
        })
      }

      onSelect({
        type: 'single',
        source: syncToLibrary ? 'asset' : 'upload',
        url,
        asset_ids: assetId ? [assetId] : [],
        status: 'completed'
      })
    } catch (err) {
      toast.error('上传失败')
    } finally {
      setActiveUploads(prev => Math.max(0, prev - 1))
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="!max-w-[2200px] !w-[calc((100vw-320px)*0.85)] h-[85vh] min-h-[700px] flex flex-col p-0 overflow-hidden z-[110] left-[calc(50%-160px)] translate-x-[-50%]" 
        aria-describedby={undefined}
      >
        <DialogHeader className="px-6 pt-6 pb-2 border-b">
          <DialogTitle>添加图片</DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <div className="px-6 py-2 border-b bg-muted/20">
            <TabsList className="grid w-full grid-cols-4 max-w-lg">
              <TabsTrigger value="library" className="gap-2">
                <Library className="w-4 h-4" /> 素材库
              </TabsTrigger>
              <TabsTrigger value="collage" className="gap-2">
                <LayoutGrid className="w-4 h-4" /> 拼图
              </TabsTrigger>
              <TabsTrigger value="ai-gen" className="gap-2">
                <Sparkles className="w-4 h-4" /> AI 生图
              </TabsTrigger>
              <TabsTrigger value="upload" className="gap-2">
                <Upload className="w-4 h-4" /> 本地上传
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 min-h-0">
            {/* 素材库 Tab */}
            <TabsContent value="library" className="h-full m-0 flex gap-0">
              <div className="w-[30%] p-0 border-r flex flex-col">
                <div className="p-4 border-b bg-muted/5">
                  <span className="text-sm font-medium text-muted-foreground">素材库 ({assets.length})</span>
                </div>
                <ScrollArea className="flex-1">
                  <div className="grid grid-cols-3 gap-2 p-2">
                    {assets.map(asset => {
                      const isUsed = (asset.usedInNoteIds?.length ?? 0) > 0 || assetIdsInNotes.has(asset.id)
                      const isSelected = selectedLibraryAsset?.id === asset.id
                      return (
                        <div
                          key={asset.id}
                          className={cn(
                            "aspect-[3/4] bg-muted rounded overflow-hidden cursor-pointer relative group transition-all",
                            isSelected ? "ring-2 ring-primary shadow-lg scale-[1.02] z-10" : "hover:opacity-90"
                          )}
                          onClick={() => setSelectedLibraryAsset(asset)}
                        >
                          <img
                            src={asset.thumbnail_url || asset.url}
                            className={cn("w-full h-full object-cover transition-opacity", isUsed && "opacity-60")}
                          />
                          {isUsed && (
                            <div className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-blue-500/80 z-10 shadow-sm" />
                          )}
                          {isSelected && (
                            <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                              <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center shadow-lg">
                                <CheckCircle className="w-4 h-4" />
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </ScrollArea>
              </div>
              
              <div className="w-[70%] bg-muted/20 flex flex-col">
                <div className="flex-1 flex items-center justify-center p-8 min-h-0">
                  {selectedLibraryAsset ? (
                    <div className="aspect-[3/4] h-full max-h-full rounded-lg overflow-hidden flex items-center justify-center">
                      <img src={selectedLibraryAsset.url} className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground">
                      <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                        <ImageIcon className="w-12 h-12 opacity-40" />
                      </div>
                      <p className="text-lg font-medium">预览区域</p>
                      <p className="text-sm mt-1">从左侧选择一张图片以预览</p>
                    </div>
                  )}
                </div>

                {selectedLibraryAsset && (
                  <div className="p-4 border-t bg-muted/5 flex flex-col items-center gap-4">
                    <div className="w-full max-w-[750px] flex flex-col gap-1 text-center">
                      <p className="text-sm font-bold truncate">{selectedLibraryAsset.title || selectedLibraryAsset.filename}</p>
                      {selectedLibraryAsset.tags && selectedLibraryAsset.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1 justify-center">
                          {selectedLibraryAsset.tags.slice(0, 5).map(tag => (
                            <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-background border rounded text-muted-foreground">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button 
                      className="w-full max-w-[750px] h-11 text-base font-bold" 
                      onClick={() => {
                        onSelect({
                          type: 'single',
                          source: 'asset',
                          url: selectedLibraryAsset.url,
                          asset_ids: [selectedLibraryAsset.id],
                          status: 'completed'
                        })
                      }}
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      确认添加此图片
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* 拼图 Tab */}
            <TabsContent value="collage" className="h-full m-0 flex gap-0">
              <div className="w-[30%] p-0 border-r flex flex-col">
                <div className="p-4 border-b bg-muted/5 flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">选择素材 ({selectedCollageAssets.length})</span>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedCollageAssets([])} disabled={selectedCollageAssets.length === 0}>
                    清空
                  </Button>
                </div>
                <ScrollArea className="flex-1">
                  <div className="grid grid-cols-3 gap-2 p-2">
                    {assets.map(asset => {
                      const isSelected = !!selectedCollageAssets.find(a => a.id === asset.id)
                      const order = selectedCollageAssets.findIndex(a => a.id === asset.id) + 1
                      return (
                        <div
                          key={asset.id}
                          className={cn(
                            "aspect-square rounded overflow-hidden cursor-pointer relative transition-all",
                            isSelected ? "ring-2 ring-primary z-10 scale-[1.02]" : "hover:opacity-90"
                          )}
                          onClick={() => handleToggleCollageAsset(asset)}
                        >
                          <img src={asset.thumbnail_url || asset.url} className="w-full h-full object-cover" />
                          {isSelected && (
                            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                              <div className="w-5 h-5 rounded-full bg-primary text-white text-[10px] flex items-center justify-center font-bold shadow-sm">
                                {order}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </ScrollArea>
                <div className="p-4 border-t bg-muted/5 flex flex-col gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block font-medium">拼图布局</Label>
                    <div className="grid grid-cols-3 gap-1">
                      {COLLAGE_LAYOUTS.map(l => (
                        <Button
                          key={l.id}
                          variant={selectedLayout === l.id ? 'default' : 'outline'}
                          size="sm"
                          className="h-8 text-[11px]"
                          onClick={() => setSelectedLayout(l.id)}
                        >
                          {l.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <Button 
                    className="h-11 w-full font-bold" 
                    onClick={handleGenerateCollage} 
                    disabled={isCollageGenerating || selectedCollageAssets.length === 0}
                  >
                    {isCollageGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    生成预览
                  </Button>
                </div>
              </div>
              <div className="w-[70%] bg-muted/20 flex flex-col">
                <div className="flex-1 flex items-center justify-center p-8 min-h-0">
                  {previewUrl ? (
                    <div className="aspect-[3/4] h-full max-h-full rounded-lg overflow-hidden flex items-center justify-center">
                      <img src={previewUrl} className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground">
                      <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                        <ImageIcon className="w-12 h-12 opacity-40" />
                      </div>
                      <p className="text-lg font-medium">预览区域</p>
                      <p className="text-sm mt-1">选择素材和布局后生成预览</p>
                    </div>
                  )}
                </div>

                {previewUrl && (
                  <div className="p-4 border-t bg-muted/5 flex justify-center">
                    <Button className="w-full max-w-[750px] h-11 text-base font-bold" onClick={handleConfirmCollage} disabled={isUploading}>
                      {isUploading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Plus className="w-5 h-5 mr-2" />}
                      确认添加此拼图
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* AI 生图 Tab */}
            <TabsContent value="ai-gen" className="h-full m-0 flex gap-0">
              <div className="w-[30%] p-0 border-r flex flex-col">
                <div className="p-4 border-b bg-muted/5 flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">选择素材 ({selectedAiGenAssets.length}/9)</span>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedAiGenAssets([])} disabled={selectedAiGenAssets.length === 0}>
                    清空
                  </Button>
                </div>
                <ScrollArea className="flex-1">
                  <div className="grid grid-cols-3 gap-2 p-2">
                    {assets.map(asset => {
                      const isSelected = !!selectedAiGenAssets.find(a => a.id === asset.id)
                      const order = selectedAiGenAssets.findIndex(a => a.id === asset.id) + 1
                      return (
                        <div
                          key={asset.id}
                          className={cn(
                            "aspect-square rounded overflow-hidden cursor-pointer relative transition-all",
                            isSelected ? "ring-2 ring-primary z-10 scale-[1.02]" : "hover:opacity-90"
                          )}
                          onClick={() => handleToggleAiGenAsset(asset)}
                        >
                          <img src={asset.thumbnail_url || asset.url} className="w-full h-full object-cover" />
                          {isSelected && (
                            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                              <div className="w-5 h-5 rounded-full bg-primary text-white text-[10px] flex items-center justify-center font-bold shadow-sm">
                                {order}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </ScrollArea>
                <div className="p-4 border-t bg-muted/5 flex flex-col gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block font-medium">Prompt (必填)</Label>
                    <Textarea 
                      placeholder="输入生图 Prompt，例如：生成一张复古风格的火锅店环境图..." 
                      className="min-h-[100px] resize-none text-sm"
                      value={aiGenPrompt}
                      onChange={e => setAiGenPrompt(e.target.value)}
                    />
                  </div>
                  <Button 
                    className="h-11 w-full font-bold" 
                    onClick={handleGenerateAiImage} 
                    disabled={isAiGenerating || !aiGenPrompt.trim()}
                  >
                    {isAiGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    生成预览
                  </Button>
                </div>
              </div>
              <div className="w-[70%] bg-muted/20 flex flex-col">
                <div className="flex-1 flex items-center justify-center p-8 min-h-0">
                  {aiGenPreviewUrl ? (
                    <div className="aspect-[3/4] h-full max-h-full rounded-lg overflow-hidden flex items-center justify-center">
                      <img src={aiGenPreviewUrl} className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground">
                      <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                        <Sparkles className="w-12 h-12 opacity-40" />
                      </div>
                      <p className="text-lg font-medium">AI 预览区域</p>
                      <p className="text-sm mt-1">输入 Prompt 并点击生成预览</p>
                    </div>
                  )}
                </div>

                {aiGenPreviewUrl && (
                  <div className="p-4 border-t bg-muted/5 flex justify-center">
                    <Button className="w-full max-w-[750px] h-11 text-base font-bold" onClick={handleConfirmAiImage} disabled={isUploading}>
                      {isUploading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Plus className="w-5 h-5 mr-2" />}
                      确认添加此图片
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* 上传 Tab */}
            <TabsContent value="upload" className="h-full m-0 p-12 flex flex-col items-center justify-center gap-8">
              <div 
                className="w-full max-w-md aspect-video border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-4 bg-muted/5 hover:bg-muted/10 cursor-pointer transition-colors"
                onClick={handleUploadClick}
              >
                {activeUploads > 0 ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">
                      正在处理中...
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Upload className="w-6 h-6 text-primary" />
                    </div>
                    <div className="text-center">
                      <p className="font-medium">点击上传本地图片</p>
                      <p className="text-xs text-muted-foreground mt-1">支持 JPG, PNG, WebP 格式</p>
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox id="sync-upload" checked={syncToLibrary} onCheckedChange={v => setSyncToLibrary(!!v)} />
                <Label htmlFor="sync-upload" className="text-sm cursor-pointer">同步传入素材库 (完成后自动进行 AI 分析)</Label>
              </div>

              <input 
                type="file" 
                className="hidden" 
                ref={fileInputRef} 
                accept="image/*" 
                onChange={handleFileChange} 
              />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
